import {betterMemo} from "js/common/utils/more-memo";
import {getQueryParam, setQueryParam, useQueryParamChange} from "js/admin/kpis/edit-kpis/utils";
import Immutable from "immutable";
import * as Users from "js/common/users";
import currentClient from "js/common/repo/backbone/current-client";
import React from "react";
import Error from "js/common/views/error";
import Config from "js/admin/kpis/edit-kpis/tabs/config";
import EditSimpleSumColumns from "js/admin/kpis/edit-kpis/tabs/edit-simple-sum-columns";
import EditColumns from "js/admin/kpis/edit-kpis/tabs/edit-columns";
import EditConfig from "js/admin/kpis/edit-kpis/tabs/edit-config";
import DateSettingsSection from "js/admin/kpis/edit-kpis/tabs/date-settings-section";
import CoreTypeFiltersEditor from "js/admin/kpis/edit-kpis/tabs/core-type-filters-editor";
import {ForwardReportInheritedConfig, ParentWarning} from "js/admin/kpis/edit-kpis/warnings";
import JsonField from "js/common/views/inputs/formatted-json-field";
import {ConfigurableTest, SimpleTest} from "js/admin/kpis/edit-kpis/tabs/test";
import OtherConfigEditor from "js/admin/kpis/edit-kpis/tabs/other-config-editor";
import {hasTestError} from "js/admin/kpis/edit-kpis/validation";
import {SingleMetricImportExport} from "js/admin/kpis/edit-kpis/tabs/import-export";
import OwnershipSection from "js/admin/kpis/edit-kpis/tabs/ownership";
import MetricRelationships from "js/admin/kpis/edit-kpis/tabs/relationships/relationships";
import Explanation from "js/admin/kpis/edit-kpis/tabs/explanation";
import Tabs from "js/common/views/tabs";

const rootChangeWarning = "The root entity on this Metric has been changed. Columns and filters have been reset to default.";

const TabTitle = ({theme, icon, text, hasError, hasWarning}) => <span data-test-id={`kpi-tab-${text}`}>
  <i className={`fa fa-${icon}`} style={{marginRight: "0.3rem"}} />
  {text}
  {(hasError || hasWarning) && <i
      className={`fa fa-exclamation`}
      style={{
        color: hasError ? "red" : theme.palette.primary.main,
        marginLeft: "0.5rem"
      }} />}
</span>;

const getTabIds = (isEditor, isCube19User, clientHasEdit, isLockedByOnboarding, queryType, hasNewConfig, hasRelationships, hasExplanationsEnabled, isAdminUser) => {
  let tabIds = Immutable.List();
  // all admin users get columns
  tabIds = tabIds.push("COLUMNS");

  if (!isLockedByOnboarding && ((isEditor && isCube19User) || clientHasEdit)) {
    if (hasNewConfig) {
      tabIds = tabIds.push("CONFIG");
    } else {
      if (isEditor && isCube19User) {
        // legacy tabs
        tabIds = tabIds.push("GENERAL");
        if (queryType) {
          tabIds = tabIds.push("DATE");
          if (queryType !== "SIMPLE_SUM" && queryType !== "FORWARD_REPORT") {
            tabIds = tabIds.push("FAST_FILTER");
          }
          tabIds = tabIds.push("OTHER");
          if (queryType !== "SIMPLE_SUM" && queryType !== "FORWARD_REPORT") {
            tabIds = tabIds.push("OWNERSHIP");
          }
        }
      } else {
        // external users always get the config tab if they are an editor
        tabIds = tabIds.push("CONFIG");
      }
    }

    tabIds = tabIds.push("TEST");
    if (isEditor && isCube19User) {
      if (queryType === "FORWARD_REPORT") {
        tabIds = tabIds.push("ACTIONABLE_INSIGHT");
      }
      tabIds = tabIds.push("IMPORT_EXPORT");
    }
  }

  if (hasRelationships) {
    tabIds = tabIds.push("RELATIONSHIPS");
  }
  if (hasExplanationsEnabled || isAdminUser) {
    tabIds = tabIds.push("EXPLANATION");
  }
  return tabIds;
};

const getAllRootNodes = (kpi, kpiIdToContributingKpis, kpiIdToDependentKpis) => {
  const startingNode = Immutable.Map({
    id: kpi.get("id"),
    name: `${kpi.get("name")}${kpi.get("deleted") ? " (deleted)" : ""}`
  });

  let [leafNodes, cyclingKpis] = getTerminalNodes(startingNode, kpiIdToDependentKpis, "down");
  let allRootNodes = Immutable.Set();

  if (!cyclingKpis) {
    leafNodes.every(leaf => {
      let [rootNodes, cyclingKpisFromLeaf] = getTerminalNodes(leaf, kpiIdToContributingKpis, "up");
      allRootNodes = allRootNodes.concat(rootNodes);
      cyclingKpis = cyclingKpisFromLeaf;
      return !cyclingKpisFromLeaf;
    });
  }

  return [allRootNodes, cyclingKpis];
};

const getTerminalNodes = (startingNode, kpiIdToNextGeneration, direction) => {
  let currentKpi = startingNode;
  let paths = Immutable.Set();
  let terminalNodes = Immutable.Set();
  let visitedNodes = Immutable.List();
  let cyclingKpis = null;

  while (currentKpi) {
    if (visitedNodes.includes(currentKpi)) {
      if (direction === "down") {
        terminalNodes = terminalNodes.add(currentKpi);
      } else {
        cyclingKpis = visitedNodes
            .push(currentKpi)
            .slice(visitedNodes.indexOf(currentKpi));
      }
      break;
    } else {
      visitedNodes = visitedNodes.push(currentKpi);
      const nextGenKpis = kpiIdToNextGeneration.get(currentKpi.get("id"));
      if (nextGenKpis) {
        if (nextGenKpis.size > 1) {
          paths = paths.union(nextGenKpis.rest().map(kpi => Immutable.Map({kpi, visitedNodes})));
        }
        currentKpi = nextGenKpis.first(Immutable.Map());
      } else {
        terminalNodes = terminalNodes.add(currentKpi);
        if (!paths.isEmpty()) {
          currentKpi = paths.first().get("kpi");
          visitedNodes = paths.first().get("visitedNodes");
          paths = paths.rest();
        } else {
          currentKpi = null;
        }
      }
    }
  }
  return [terminalNodes, cyclingKpis];
};

const TabbedKpiEditor = betterMemo(
    {debug: false, displayName: "TabbedKpiEditor"},
    ({
      drawerContentHeight,
      theme,
      isCube19User,
      clientHasEdit,
      isLockedByOnboarding,
      columns,
      submitError,
      onColumnsChange,
      onUnmirror,
      kpisWithMatchingRootEntity,
      masterMetricsAvailableToCombineWith,
      kpisThatShareColumnsWithThisKpi,
      kpi,
      entityTypeToLabel,
      entityType,
      parentCombinedKpi,
      combinedKpi,
      combineError,
      onKpiChange,
      kpiIsParent,
      testResult,
      onTest,
      testConfig,
      onTestConfigChange,
      idToEntityColumn,
      idToTemplate,
      availableOwners,
      availableServerColumns,
      typeToGroupingEntity,
      rootChanged,
      kpiIdToMasterKpis,
      onDependencyClick,
      queryType,
      columnsQueryType,
      hasNewConfig,
      isEditor,
      hasRelationships,
      kpiIdToDependentKpis,
      kpiIdToContributingKpis,
      requiresExplanationUpdate,
      tabId,
      searchFocused,
      filterText,
      onChangeTab,
      onClearRequiresExplanationUpdate,
      sumDoubleSplitsAvailable,
      sumSplitAvailable,
      aggregationTypes,
      trendTypes,
      nameToCount
    }) => {

      const kpiIsChild = !!(kpi.get("combineWithKpiId") ?? kpi.get("combineWithMasterMetricType"));
      const parentHasForwardReportConfig = parentCombinedKpi && getQueryParam(parentCombinedKpi, "forwardReport");
      const kpiHasForwardReportConfig = getQueryParam(kpi, "forwardReport");
      const template = idToTemplate.get(kpi.get("templateId")) || Immutable.Map();
      const handleCoreTypeFiltersChange = useQueryParamChange(kpi, onKpiChange, "coreTypeFilters");
      const kpisAvailableToCombineWith = kpisWithMatchingRootEntity
          .filter(k => !isNaN(k.get("id")) && k.get("id") !== kpi.get("id"));
      const currentUser = Users.getCurrentUser();

      const tabIds = getTabIds(
          isEditor,
          isCube19User,
          clientHasEdit,
          isLockedByOnboarding,
          queryType,
          hasNewConfig,
          hasRelationships,
          currentClient.areExplanationsEnabled(),
          Users.canAccessApp(currentUser, "kpi_admin")
      );

      const defaultTabIndex = hasNewConfig ? 1 : 0;
      const tabIndex = tabId ? tabIds.indexOf(tabId) : defaultTabIndex;

      const [rootNodes, cyclingKpis] = React.useMemo(
          () =>
              getAllRootNodes(kpi, kpiIdToContributingKpis, kpiIdToDependentKpis),
          [kpi, kpiIdToContributingKpis, kpiIdToDependentKpis]);

      const getExplanationWarning = React.useCallback(() => <Error
          text={<div>You have changed this metric's config. Please check the <span
              style={{
                cursor: "pointer",
                textDecoration: "underline"
              }} onClick={() => onChangeTab(kpi.get("id"), "EXPLANATION")}>Metric Explanation</span> to ensure it is up
            to
            date.</div>}
          type="warn"
          style={{marginTop: 0}} />, [kpi, onChangeTab]);


      const tabsToGetTab = Immutable.Map({
        "CONFIG": () => {
          return {
            title: <TabTitle theme={theme} icon="info" text="Config" hasError={!!combineError || !!submitError} />,
            content: <Config
                queryType={queryType}
                kpi={kpi}
                entityTypeToLabel={entityTypeToLabel}
                entityType={entityType}
                typeToGroupingEntity={typeToGroupingEntity}
                availableOwners={availableOwners}
                availableServerColumns={availableServerColumns}
                columns={(columns || Immutable.List()).filter(c => c.get("isFilterable"))}
                onChange={onKpiChange}
                kpisAvailableToCombineWith={kpisAvailableToCombineWith}
                kpiIdToDependentKpis={kpiIdToDependentKpis}
                rootNodes={rootNodes}
                cyclingKpis={cyclingKpis}
                onKpiChange={onKpiChange}
                searchFocused={searchFocused}
                filterText={filterText}
                parentKpi={parentCombinedKpi}
                idToTemplate={idToTemplate}
                combinedKpi={combinedKpi}
                combineError={combineError}
                sumDoubleSplitsAvailable={sumDoubleSplitsAvailable}
                sumSplitAvailable={sumSplitAvailable}
                aggregationTypes={aggregationTypes}
                trendTypes={trendTypes}
                nameToCount={nameToCount}
            />
          };
        },
        "COLUMNS": () => columnsQueryType === "SIMPLE_SUM" ? {
          title: <TabTitle theme={theme} icon="list" text="Columns" hasError={!!submitError} />,
          content: <EditSimpleSumColumns
              columns={columns}
              submitError={submitError}
              columnsEditable={template.get("columnsEditable")}
              onChange={onColumnsChange}
              rootChangedWarning={rootChanged && rootChangeWarning} />
        } : {
          title: <TabTitle theme={theme} icon="list" text="Columns" hasError={!!submitError} />,
          content: <EditColumns
              kpi={kpi}
              columns={columns}
              submitError={submitError}
              columnsEditable={template.get("columnsEditable")}
              onChange={onColumnsChange}
              onMirror={kpiId => onKpiChange(kpi.set("columnsKpiId", kpiId))}
              onUnmirror={() => onUnmirror(kpi)}
              kpisAvailableToMirror={kpisAvailableToCombineWith}
              kpisThatShareColumnsWithThisKpi={kpisThatShareColumnsWithThisKpi}
              rootChangedWarning={rootChanged && rootChangeWarning} />
        },
        "GENERAL": () => {
          return {
            title: <TabTitle theme={theme} icon="info" text="General" hasError={!!combineError || !!submitError} />,
            content: <EditConfig
                combineError={combineError}
                combinedKpi={combinedKpi}
                entityTypeToLabel={entityTypeToLabel}
                getExplanationWarning={getExplanationWarning}
                idToTemplate={idToTemplate}
                isCube19User={isCube19User}
                kpi={kpi}
                kpiIdToMasterKpis={kpiIdToMasterKpis}
                kpiIsChild={kpiIsChild}
                kpiIsParent={kpiIsParent}
                kpisAvailableToCombineWith={kpisAvailableToCombineWith}
                masterMetricsAvailableToCombineWith={masterMetricsAvailableToCombineWith}
                onChange={onKpiChange}
                onTest={onTest}
                parentCombinedKpi={parentCombinedKpi}
                requiresExplanationUpdate={requiresExplanationUpdate}
                rootChangedWarning={rootChanged && rootChangeWarning}
                submitError={submitError}
                testResult={testResult}
                typeToGroupingEntity={typeToGroupingEntity}
            />
          };
        },
        "DATE": () => {
          return {
            title: <TabTitle theme={theme} icon="clock-o" text="Date" />,
            content: <DateSettingsSection
                idToEntityColumn={idToEntityColumn}
                idToTemplate={idToTemplate}
                typeToGroupingEntity={typeToGroupingEntity}
                kpi={kpi}
                combinedKpi={combinedKpi}
                onKpiChange={onKpiChange}
                kpiIsParent={kpiIsParent}
                kpiIsChild={kpiIsChild}
                testResult={testResult}
                onTest={onTest}
                queryType={queryType}
                rootChangedWarning={rootChanged && rootChangeWarning}
                requiresExplanationUpdate={requiresExplanationUpdate}
                getExplanationWarning={getExplanationWarning} />
          };
        },
        "FAST_FILTER": () => {
          return {
            title: <TabTitle theme={theme} icon="filter" text="Fast Filter" />,
            content: <CoreTypeFiltersEditor
                combinedKpi={combinedKpi}
                idToEntityColumn={idToEntityColumn}
                typeToGroupingEntity={typeToGroupingEntity}
                rootGroupingEntity={kpi.get("readOnlyRootGroupingEntity")}
                filters={getQueryParam(kpi, "coreTypeFilters", new Immutable.List())}
                onFiltersChange={handleCoreTypeFiltersChange}
                kpiIsParent={kpiIsParent}
                kpiIsChild={kpiIsChild}
                testResult={testResult}
                onTest={onTest}
                queryType={queryType}
                rootChangedWarning={rootChanged && rootChangeWarning}
                requiresExplanationUpdate={requiresExplanationUpdate}
                getExplanationWarning={getExplanationWarning} />
          };
        },
        "ACTIONABLE_INSIGHT": () => {
          const forwardReport = hasNewConfig ?
              kpi.get("config").get("forwardReport") :
              getQueryParam(kpi, "forwardReport");
          const handleChange = hasNewConfig ?
              forwardReport => onKpiChange(kpi.setIn(["config", "forwardReport"], forwardReport)) :
              forwardReport => onKpiChange(setQueryParam(kpi, "forwardReport", forwardReport));
          return {
            title: <TabTitle theme={theme} icon="forward" text="Actionable Insight" />,
            content: <div style={{padding: "1rem"}}>
              {kpiIsParent && <ParentWarning />}
              {requiresExplanationUpdate && getExplanationWarning()}
              {parentHasForwardReportConfig && !kpiHasForwardReportConfig && <ForwardReportInheritedConfig />}
              <JsonField
                  label="Forward Report (JSON)"
                  value={forwardReport || Immutable.Map()}
                  onChange={handleChange}
                  style={{flex: 1, marginBottom: "1rem", marginRight: "1rem"}} />
              <SimpleTest
                  testResult={testResult}
                  onTest={onTest} />
            </div>
          };
        },
        "OTHER": () => {
          return {
            title: <TabTitle theme={theme} icon="globe" text="Other" />,
            content: <OtherConfigEditor
                theme={theme}
                kpi={kpi}
                combinedKpi={combinedKpi}
                onKpiChange={onKpiChange}
                kpiIsParent={kpiIsParent}
                kpiIsChild={kpiIsChild}
                testResult={testResult}
                onTest={onTest}
                requiresExplanationUpdate={requiresExplanationUpdate}
                getExplanationWarning={getExplanationWarning} />
          };
        },
        "TEST": () => {
          return {
            title: <TabTitle
                theme={theme}
                icon="tachometer"
                text="Test"
                hasError={!!testResult && hasTestError(testResult)} />,
            content: <ConfigurableTest
                testConfig={testConfig}
                onConfigChange={onTestConfigChange}
                template={template}
                testResult={testResult}
                onTest={onTest} />
          };
        },
        "IMPORT_EXPORT": () => {
          return {
            title: <TabTitle theme={theme} icon="file" text="Import / Export" />,
            content: <SingleMetricImportExport
                kpi={kpi}
                onKpiChange={onKpiChange}
                kpiIsParent={kpiIsParent}
                columns={columns}
                onColumnsChange={onColumnsChange}
                requiresExplanationUpdate={requiresExplanationUpdate}
                getExplanationWarning={getExplanationWarning} />
          };
        },
        "OWNERSHIP": () => {
          return {
            title: <TabTitle theme={theme} icon="link" text="Ownership" />,
            content: <OwnershipSection
                theme={theme}
                kpi={kpi}
                onKpiChange={onKpiChange}
                idToEntityColumn={idToEntityColumn}
                availableOwners={availableOwners}
                requiresExplanationUpdate={requiresExplanationUpdate}
                getExplanationWarning={getExplanationWarning}
            />
          };
        },
        "RELATIONSHIPS": () => {
          return {
            title: <TabTitle theme={theme} icon="project-diagram" text="Relationships" hasError={!!cyclingKpis} />,
            content: <MetricRelationships
                drawerContentHeight={drawerContentHeight}
                kpiIdToDependentKpis={kpiIdToDependentKpis}
                kpiIdToContributingKpis={kpiIdToContributingKpis}
                onDependencyClick={onDependencyClick}
                kpi={kpi}
                cyclingKpis={cyclingKpis}
                rootNodes={rootNodes}
            />
          };
        },
        "EXPLANATION": () => {
          let columnNameToKpi = Immutable.fromJS({
            "Parent KPI Value": parentCombinedKpi,
            "This KPI Value": kpi,
            "Final KPI Value": combinedKpi
          });
          if (!parentCombinedKpi) {
            columnNameToKpi = columnNameToKpi.deleteAll(["Parent KPI Value", "This KPI Value"]);
          }
          return {
            title: <TabTitle
                theme={theme}
                icon={"question"}
                text={"Explanation"}
                hasWarning={requiresExplanationUpdate} />,
            content: <Explanation
                kpi={kpi}
                onKpiChange={onKpiChange}
                showConfigOverview={isEditor && isCube19User}
                columnNameToMap={columnNameToKpi.map(k => k.deleteAll(["readOnlyCombined", "order"]))}
                idToTemplate={idToTemplate}
                entityTypeToLabel={entityTypeToLabel}
                requiresExplanationUpdate={requiresExplanationUpdate}
                onClearRequiresExplanationUpdate={onClearRequiresExplanationUpdate} />
          };
        }
      });

      const generatedTabs = tabIds.map(tab => tabsToGetTab.get(tab)()).toArray();
      return <Tabs
          key={`drawer-tabs-${kpi.get("id")}`}
          containerStyle={{height: "auto"}}
          selectedIndex={tabIndex}
          onChangeTab={tabIndex => onChangeTab(kpi.get("id"), tabIds.get(tabIndex))}
          tabs={generatedTabs} />;
    });

export default TabbedKpiEditor;
